import { ChangeEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import useFetchSystemTemplates from 'components/domain/automation/systemTemplates/useFetchSystemTemplates';
import useSystemTemplateToggleUtils from 'components/domain/automation/systemTemplates/useSystemTemplateToggleUtils';
import useToggleSystemTemplatesMutation from 'components/domain/automation/systemTemplates/useToggleSystemTemplatesMutation';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import Switch from 'components/switch/Switch';
import { ToggleTemplateAction } from 'models/automation/SystemTemplates';
import { ALL_TEMPLATE_ROWS } from '../SystemTemplatesTab.constants';
import { SystemTemplatesTabGlobalToggleStyled } from '../SystemTemplatesTab.styles';

const TEMPLATE_KEYS_WITH_STATUS = ALL_TEMPLATE_ROWS.filter(
  (row) => row.defaultStatus !== undefined,
).map(({ key }) => key);

const SystemTemplatesTabGlobalToggle = () => {
  const { t } = useTranslation();
  const { onMutateFactory, onError, onSettled } =
    useSystemTemplateToggleUtils();

  const { mutate: toggleSystemTemplates, isLoading: isMutating } =
    useToggleSystemTemplatesMutation({
      onMutate: (variables) => {
        return onMutateFactory({
          enabled: variables.action === ToggleTemplateAction.ENABLE,
          keys: TEMPLATE_KEYS_WITH_STATUS,
        })();
      },
      onError,
      onSettled,
    });

  const { data: systemTemplates, isLoading } = useFetchSystemTemplates();

  const isEveryTemplateDisabled = ALL_TEMPLATE_ROWS.every(
    ({ key, defaultStatus }) => {
      if (defaultStatus === undefined) {
        return true;
      }

      const template = systemTemplates?.find((o) => o.key === key);
      return template && !template.enabled;
    },
  );

  const onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const isInputChecked = event.target.checked;

    if (isInputChecked) {
      toggleSystemTemplates({
        templateKeys: TEMPLATE_KEYS_WITH_STATUS,
        action: ToggleTemplateAction.ENABLE,
      });
    } else {
      toggleSystemTemplates({
        templateKeys: TEMPLATE_KEYS_WITH_STATUS,
        action: ToggleTemplateAction.DISABLE,
      });
    }
  };

  return (
    <SystemTemplatesTabGlobalToggleStyled>
      <span>{t('pageAutomations.systemTemplates.toggleAll')}</span>

      {isLoading ? (
        <LoadingPlaceholder margin="9px" linesCount={1.57} width="44px" />
      ) : (
        <Switch
          checked={!isEveryTemplateDisabled}
          disabled={isMutating}
          data-testid="toggle-all"
          name="toggle-all"
          onChange={onChange}
        />
      )}
    </SystemTemplatesTabGlobalToggleStyled>
  );
};

export default SystemTemplatesTabGlobalToggle;
